// components/movements/RecordReturnModal.tsx
import React, { useState } from "react";
import { ArtworkMovementDto, RecordReturnRequest } from "../../models/Movement";
import { useUser } from '../../components/UserContext';
import { useMovementsApi } from "../../hooks/useMovementsApi";

import type { ArtworkStatusRefData } from "../../models/ArtworkStatus";
import "./MovementsStyleSheet.css";
interface Props {
    movement: ArtworkMovementDto;
    statuses: ArtworkStatusRefData[];
    onClose: () => void;
    onReturned: () => void;
}

const RETURN_CODES = ["COLL", "AVAIL", "NFS", "STOR"];

export const RecordReturnModal: React.FC<Props> = ({
    movement,
    statuses,
    onClose,
    onReturned,
}) => {
    const api = useMovementsApi();
    const { getAccessToken } = useUser();

    const returnStatuses = statuses.filter(s => RETURN_CODES.includes(s.code));

    const [returnStatusId, setReturnStatusId] = useState<number>(
        returnStatuses[0]?.artworkStatusId ?? 0);
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    const handleSubmit = async () => {
        if (!returnStatusId) return;

        const accessToken = await getAccessToken();

        if (accessToken) {
            setSaving(true);
            setError(null);
            try {
                const request: RecordReturnRequest = {
                    artworkId: movement.artworkId,
                    returnToStatusId: returnStatusId,
                    notes: notes || undefined,
                };
                await api.recordReturn(request, accessToken);
                onReturned();
                onClose();
            } catch (e: any) {
                setError(e.message);
            } finally {
                setSaving(false);
            }
        }
    };

    const formattedDeparture = movement.statusDate
        ? new Date(movement.statusDate).toLocaleDateString("en-GB", {
            day: "numeric", month: "short", year: "numeric"
        })
        : "—";

    const formattedExpected = movement.expectedReturnDate
        ? new Date(movement.expectedReturnDate).toLocaleDateString("en-GB", {
            day: "numeric", month: "short", year: "numeric"
        })
        : "—";

    return (
        <div className="cm-overlay" onClick={handleOverlayClick}>
            <div className="cm-dialog" role="dialog" aria-modal="true"
                aria-labelledby="rr-title">

                {/* Header */}
                <div className="cm-header">
                    <div className="cm-header-text">
                        <h2 id="rr-title">Record Return</h2>
                        <p className="cm-header-sub">
                            Confirm the artwork has been returned and update its status
                        </p>
                    </div>
                    <button className="cm-close" onClick={onClose} aria-label="Close">×</button>
                </div>

                {/* Body */}
                <div className="cm-body">

                    {/* LEFT — artwork context + movement summary */}
                    <aside className="cm-sidebar">
                        {movement.thumbnailUrl ? (
                            <img src={movement.thumbnailUrl} alt={movement.artworkTitle}
                                className="cm-thumb" />
                        ) : (
                            <div className="cm-thumb-placeholder">
                                <span>No Image</span>
                            </div>
                        )}

                        <div className="cm-artwork-meta">
                            <div className="cm-artwork-title">{movement.artworkTitle}</div>
                            {movement.artistName && (
                                <div className="cm-artwork-artist">{movement.artistName}</div>
                            )}
                            {movement.referenceNumber && (
                                <div className="cm-artwork-ref">{movement.referenceNumber}</div>
                            )}
                        </div>

                        {/* Current movement summary */}
                        <div className="rr-movement-summary">
                            <div className="rr-summary-title">Current Movement</div>

                            <div className="rr-summary-row">
                                <span className="rr-summary-label">Status</span>
                                <span className={`badge badge--movement-${movement.extendedInfoType?.toLowerCase() ?? "none"}`}>
                                    {movement.statusName}
                                </span>
                            </div>

                            {movement.destination && (
                                <div className="rr-summary-row">
                                    <span className="rr-summary-label">Destination: </span>
                                    <span className="rr-summary-value">{movement.destination}</span>
                                </div>
                            )}

                            {movement.contact && (
                                <div className="rr-summary-row">
                                    <span className="rr-summary-label">Contact: </span>
                                    <span className="rr-summary-value">{movement.contact}</span>
                                </div>
                            )}

                            <div className="rr-summary-row">
                                <span className="rr-summary-label">Departed: </span>
                                <span className="rr-summary-value">{formattedDeparture}</span>
                            </div>

                            <div className="rr-summary-row">
                                <span className="rr-summary-label">Expected: </span>
                                <span className={`rr-summary-value ${movement.isOverdue ? "rr-summary-value--overdue" : ""}`}>
                                    {formattedExpected}
                                    {movement.isOverdue && (
                                        <span className="rr-overdue-tag">
                                            {movement.daysOverdue}d overdue
                                        </span>
                                    )}
                                </span>
                            </div>

                            {movement.insuredValue && (
                                <div className="rr-summary-row">
                                    <span className="rr-summary-label">Insured: </span>
                                    <span className="rr-summary-value">
                                        {movement.insuranceCurrency}{" "}
                                        {movement.insuredValue.toLocaleString()}
                                    </span>
                                </div>
                            )}

                            {movement.insurancePolicyRef && (
                                <div className="rr-summary-row">
                                    <span className="rr-summary-label">Policy Ref: </span>
                                    <span className="rr-summary-value">
                                        {movement.insurancePolicyRef}
                                    </span>
                                </div>
                            )}
                        </div>
                    </aside>

                    {/* RIGHT — return form */}
                    <div className="cm-form">

                        {error && (
                            <div className="cm-error">{error}</div>
                        )}

                        <fieldset className="cm-fieldset">
                            <legend>Return Details</legend>

                            <div className="cm-form-group">
                                <label className="cm-label">
                                    Return To Status <span className="cm-required">*</span>
                                </label>
                                <select className="cm-select" value={returnStatusId}
                                    onChange={e => setReturnStatusId(Number(e.target.value))}>
                                    {returnStatuses.map(s => (
                                        <option key={s.artworkStatusId}
                                            value={s.artworkStatusId}>
                                            {s.name}
                                        </option>
                                    ))}
                                </select>
                                <span className="cm-hint-inline">
                                    Select the status that best describes where the artwork
                                    is now it has been returned.
                                </span>
                            </div>
                        </fieldset>

                        <fieldset className="cm-fieldset">
                            <legend>Condition &amp; Notes</legend>

                            <div className="cm-form-group">
                                <label className="cm-label">Return Notes</label>
                                <textarea className="cm-textarea" rows={6}
                                    value={notes}
                                    onChange={e => setNotes(e.target.value)}
                                    placeholder="Condition on return, any damage observed, handling notes…" />
                            </div>
                        </fieldset>

                        {/* Confirmation callout */}
                        <div className="rr-confirm-notice">
                            <span className="rr-confirm-icon">ℹ️</span>
                            <p>
                                Recording a return will update the artwork's current status
                                and close this movement record. This action cannot be undone.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="cm-footer">
                    <button className="btn btn--ghost" onClick={onClose}
                        disabled={saving}>
                        Cancel
                    </button>
                    <button className="btn btn--primary" onClick={handleSubmit}
                        disabled={saving || !returnStatusId}>
                        {saving ? "Saving…" : "Confirm Return"}
                    </button>
                </div>
            </div>
        </div>
    );
};